export type SearchGroup = 'ai' | 'recent' | 'page' | 'action' | 'model' | 'project' | 'key' | 'doc';

export interface SearchItem {
  id: string;
  group: SearchGroup;
  title: string;
  subtitle?: string;
  /** Folded haystack. */
  text: string;
  to?: string;
  run?: () => void;
  icon?: string;
  logo?: { model: string; provider?: string; family?: string };
  /** Tie breaker. */
  boost?: number;
  /** Needs a title hit. */
  strict?: boolean;
}

export const GROUP_LABELS: Record<SearchGroup, string> = {
  ai: 'Tanya AI',
  recent: 'Terakhir dibuka',
  page: 'Halaman',
  action: 'Aksi',
  model: 'Model',
  project: 'Proyek',
  key: 'API key',
  doc: 'Dokumentasi',
};

const GROUP_ORDER: SearchGroup[] = ['ai', 'recent', 'page', 'action', 'model', 'project', 'key', 'doc'];

export const fold = (value: string) =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');

const FILLER = new Set(
  'apa apakah bagaimana gimana cara kenapa mengapa bisa bisakah berapa kapan dimana mana siapa saya aku kami kita yang di ke dari untuk dan atau ini itu dengan agar supaya tolong mau ingin perlu harus sudah belum kok sih ya pakai memakai menggunakan membuat bikin lewat kena how to what why is the a an do i can'.split(' '),
);

/** Filler dropped when possible. */
export const terms = (query: string) => {
  const words = fold(query).split(/[^a-z0-9.:_-]+/).filter(Boolean);
  const meaningful = words.filter((word) => !FILLER.has(word));
  return meaningful.length ? meaningful : words;
};

/** 0 means no match. */
export function score(item: SearchItem, words: string[]): number {
  if (words.length === 0) return 1;
  const title = fold(item.title);
  if (item.strict && !words.some((w) => title.includes(w))) return 0;
  let total = 0;
  for (const word of words) {
    if (title.startsWith(word)) total += 6;
    else if (title.split(/\s+/).some((part) => part.startsWith(word))) total += 4;
    else if (title.includes(word)) total += 3;
    else if (item.text.includes(word)) total += 1;
    else return 0;
  }
  return total + (item.boost ?? 0);
}

export interface RankedGroup {
  group: SearchGroup;
  items: SearchItem[];
}

/** Grouped, capped per group. */
export function rank(items: SearchItem[], query: string, perGroup = 5): RankedGroup[] {
  const words = terms(query);
  const scored = items
    .map((item, order) => ({ item, order, value: score(item, words) }))
    .filter((hit) => hit.value > 0)
    .sort((a, b) => b.value - a.value || a.order - b.order);
  const groups = new Map<SearchGroup, SearchItem[]>();
  for (const { item } of scored) {
    const list = groups.get(item.group) ?? [];
    if (list.length < perGroup) list.push(item);
    groups.set(item.group, list);
  }
  return GROUP_ORDER.filter((group) => groups.has(group)).map((group) => ({ group, items: groups.get(group)! }));
}

const QUESTION =
  /^(apa|apakah|bagaimana|gimana|kenapa|mengapa|cara|bisa|bisakah|berapa|kapan|dimana|di mana|siapa|model mana|how|what|why|can|where|when)\b/;

/** Natural question? */
export function looksLikeQuestion(query: string): boolean {
  const q = fold(query).trim();
  return q.endsWith('?') || QUESTION.test(q) || q.split(/\s+/).length >= 4;
}
